import React, { useState } from "react";
import { Alert, Space } from "antd";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { QuestionCircleOutlined } from "@ant-design/icons";
// graphql
import GET_MY_CREDITS from "graphql/queries/GetMyCredits";
import GET_TRIAL_PACKAGE from "graphql/queries/GetTrialPackage";
// custom hooks
import useBannerBackground from "hooks/useBannerBackground";

// common
import {
  PageContent,
  Container,
  HeadingTitle,
  SectionHeader,
  Content,
  Section,
} from "common/layout";

// components
import { PaymentMethod } from "types";
import { H4 } from "common/text";
import {
  HeadingWrapper,
  Button,
  ButtonIcon,
  PrePurchaseBanner,
  NoPaymentMethodAlert,
} from "./styles";
import CreditList from "./CreditList";
import BuyExtraCredits from "./BuyExtraCredit/BuyExtraCredits";
import ChangeSubscription from "./Subscription/ChangeSubscription";
import EditSubscription from "./Subscription/EditSubscription";
import MySubscriptionTable from "./Subscription/MySubscriptionTable";
import MyPackageTable from "./TrialPackage/MyPackageTable";
import CancelTrialPackage from "./TrialPackage/CancelTrialPackage";

const MyCredits = () => {
  const { t } = useTranslation("my_credits");

  const [currency, setCurrency] = useState<string | undefined>(undefined);

  const { data, loading } = useQuery(GET_MY_CREDITS, {
    fetchPolicy: "network-only",
  });

  const { data: trialData } = useQuery(GET_TRIAL_PACKAGE, {
    fetchPolicy: "network-only",
  });

  const { bannerBackgroundUrl } = useBannerBackground();

  if (loading) {
    return (
      <div style={{ marginTop: 64 }}>
        <Alert type="info" message={t("Loading...")} />
      </div>
    );
  }

  const {
    myCredits: { credits, totalAmount },
    me: {
      id: studentId,
      activeSubscription,
      paymentMethods,
      subscriptions,
      paymentType,
    },
  } = data;

  // Set default value for currency selector
  const isVnpay = paymentType === "vnpay";
  const FALLBACK_CURRENCY = isVnpay ? "vnd" : "usd";
  const currentCurrency: string | undefined = isVnpay
    ? FALLBACK_CURRENCY
    : subscriptions[0]?.currency;
  if (!currency) {
    setCurrency(currentCurrency || FALLBACK_CURRENCY);
  }

  const isPaymentMethodExist = paymentMethods && paymentMethods.length > 0;
  const defaultPaymentMethod =
    isPaymentMethodExist &&
    paymentMethods.find((p: PaymentMethod) => p.default);

  const isMobile = (): boolean =>
    !window.matchMedia("(min-width: 500px)").matches;

  let trialStatus = false;
  let trialNotPastDue = false;

  if (trialData?.trialPackages[0]) {
    trialStatus =
      trialData?.trialPackages[0].status === "trialing" ||
      trialData?.trialPackages[0].status === "canceled";
    const thisTime = new Date();
    const trialDue = new Date(trialData?.trialPackages[0].endDate || thisTime);
    trialNotPastDue = trialDue > thisTime;
  }

  const NoPmWarning = (
    <div>
      <NoPaymentMethodAlert
        message={
          <span>
            {t("You don't have a default payment method yet!")}{" "}
            <Link
              to="/account/my-billings"
              style={{ textDecoration: "underline" }}
            >
              {t("Set it now")} &gt;
            </Link>
          </span>
        }
        type="warning"
        showIcon
      />
      <br />
    </div>
  );

  return (
    <PageContent>
      <HeadingWrapper
        background-url={bannerBackgroundUrl}
        style={{ flexDirection: "column", alignItems: "stretch" }}
      >
        <HeadingTitle style={{ flexDirection: "row", alignItems: "stretch" }}>
          <Space
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <span>
              <Space size="middle">
                <span>
                  {t("MY CREDITS")}: {totalAmount}
                </span>

                <ButtonIcon
                  type="link"
                  shape="circle"
                  href="https://flexiclasses.com/faq-topic/using-credits/"
                  target="_blank"
                  style={{ display: "flex" }}
                  icon={
                    <QuestionCircleOutlined
                      style={{
                        fontSize: "30px",
                        color: "#fdd440",
                        margin: "20, 20",
                      }}
                    />
                  }
                />
              </Space>
            </span>
            <Button href="https://flexiclasses.com/faq/" target="_blank">
              {" "}
              {t("FLEXI STUDENT HELP")}{" "}
            </Button>
          </Space>
        </HeadingTitle>
        <br />
        <CreditList credits={credits} />
      </HeadingWrapper>
      <Container>
        <Content>
          {!defaultPaymentMethod && !isVnpay && NoPmWarning}

          {(activeSubscription || isVnpay) && (
            <Section>
              <SectionHeader>{t("BUY CREDITS")}</SectionHeader>
              {!activeSubscription && !isVnpay && (
                <Alert
                  type="warning"
                  message={t(
                    "Flexi Classes are a subscription service. You need an active subscription to be able to buy additional credits."
                  )}
                />
              )}
              <br />
              <BuyExtraCredits
                {...{
                  currency,
                  setCurrency,
                  activeSubscription,
                  currentCurrency,
                  paymentType,
                }}
              />
            </Section>
          )}

          {trialStatus && trialNotPastDue && (
            <Section>
              <SectionHeader>{t("MY PACKAGE")}</SectionHeader>
              <MyPackageTable
                activeTrialStatus={trialData?.trialPackages[0].status}
                currency={currency}
                endDate={trialData?.trialPackages[0].endDate}
              />
            </Section>
          )}

          {activeSubscription && !isVnpay && (
            <Section>
              <SectionHeader>
                {t(
                  activeSubscription.renew
                    ? "MY SUBSCRIPTION"
                    : "MY SUBSCRIPTION IS CANCELED"
                )}
              </SectionHeader>
              <MySubscriptionTable
                activeSubscription={activeSubscription}
                currency={currency}
              />
            </Section>
          )}

          <Section>
            <ChangeSubscription
              {...{
                currency,
                setCurrency,
                currentCurrency,
                defaultPaymentMethod,
                paymentType,
              }}
            />
            {!activeSubscription && (
              <PrePurchaseBanner
                style={{ textAlign: isMobile() ? "left" : "center" }}
                message={
                  <p>
                    A Flexi Subscription gives you full flexibility. You can
                    cancel your subscription at any time. All credits will
                    remain valid and you can continue to use Flexi Classes
                    without restrictions after canceling. For more info check
                    out the{" "}
                    <a
                      href="https://flexiclasses.com/faq/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <u>Flexi FAQ section</u>
                    </a>
                    .
                  </p>
                }
              />
            )}
          </Section>
          {trialStatus && trialNotPastDue && (
            <Section>
              <SectionHeader>
                {trialData?.trialPackages[0].status === "trialing"
                  ? t("CANCEL UPCOMING PURCHASE")
                  : t("REACTIVATE PURCHASE")}
              </SectionHeader>
              <CancelTrialPackage
                activeTrialStatus={trialData?.trialPackages[0].status}
                currency={trialData?.trialPackages[0].currency}
                endDate={trialData?.trialPackages[0].endDate}
                id={trialData?.trialPackages[0].id}
                priceId={trialData?.trialPackages[0].priceId}
              />
            </Section>
          )}
          {activeSubscription && activeSubscription.renew && !isVnpay && (
            <Section>
              <SectionHeader>{t("CANCEL SUBSCRIPTION")}</SectionHeader>
              <br />
              {!activeSubscription && (
                <div>
                  <H4>{t("CURRENT SUBSCRIPTION")}</H4>
                  <div style={{ marginTop: 18 }}>
                    <Alert
                      type="warning"
                      message={t(
                        "You didn't subscribe to any program yet. Subscribe by selecting one above."
                      )}
                    />
                  </div>
                </div>
              )}
              {activeSubscription && (
                <EditSubscription
                  {...{ currency, activeSubscription, studentId }}
                />
              )}
            </Section>
          )}
        </Content>
      </Container>
    </PageContent>
  );
};

export default MyCredits;
